import React, { useEffect, ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";

import { RootState } from "../../store";
import { useThemeContext } from "../../contexts/ThemeContext";
import { postNotification } from "../../actions";
import { eventTypeIds } from "../../constants";

import BoostCreatedMessage from "./messages/BoostCreatedMessage";
import DepositPendingMessage from "./messages/DepositPendingMessage";
import InvestmentAccountRelatedMessage from "./messages/InvestmentAccountRelatedMessage";
import MemberCreatedMessage from "./messages/MemberCreatedMessage";
import OperationRelatedMessage from "./messages/OperationRelatedMessage";
import PostReplyCreatedMessage from "./messages/PostReplyCreatedMessage";
import SubscriptionRelatedMessage from "./messages/SubscriptionRelatedMessage"
import UserFollowerCreatedMessage from "./messages/UserFollowerCreatedMessage";
import UserMentionCreatedMessage from "./messages/UserMentionCreatedMessage";

interface NotificationToastsDecoratorProps {
  children: ReactNode;
}

const NotificationToastsDecorator: React.FC<NotificationToastsDecoratorProps> = ({
  children,
}) => {
  const { theme } = useThemeContext();
  const dispatch = useDispatch();

  const isAuthenticated = useSelector(
    (state: RootState) => state.authentication.isAuthenticated
  );
  const userId = useSelector((state: RootState) => state.authentication.userId);
  const notificationListItems = useSelector(
    (state: RootState) =>
      state.entities.notificationListItems.byUserIdAndViewedAtIsNull[userId]
  );
  const notificationById = useSelector(
    (state: RootState) => state.entities.notifications.byId
  );

  const handleCloseClick = (notificationId: string) => {
    const notification = notificationById[notificationId];
    if (!notification) return;
    notification.viewedAt = moment().toISOString();
    dispatch(postNotification(notification));
  };

  useEffect(() => {
    if (!notificationListItems) return;

    notificationListItems.forEach((listItem) => {
      const commonProps = {
        toastId: listItem.notificationId,
        onClose: () => handleCloseClick(listItem.notificationId),
      };

      switch (listItem.eventTypeId) {
        case eventTypeIds.BOOST_CREATED:
          toast(<BoostCreatedMessage {...listItem} />, commonProps);
          break;
        case eventTypeIds.DEPOSIT_PENDING:
        case eventTypeIds.DEPOSIT_INTEREST_PENDING:
        case eventTypeIds.DEPOSIT_DIVIDEND_PENDING:
          toast(<DepositPendingMessage {...listItem} />, commonProps);
          break;
        case eventTypeIds.INVESTMENTACCOUNT_CREATED:
        case eventTypeIds.INVESTMENTACCOUNT_ENABLED:
          toast(<InvestmentAccountRelatedMessage {...listItem} />, commonProps);
          break;
        case eventTypeIds.MEMBER_CREATED:
          toast(<MemberCreatedMessage {...listItem} />, commonProps);
          break;
        case eventTypeIds.BUY_PENDING:
        case eventTypeIds.SELL_PENDING:
        case eventTypeIds.BUY_SUBMITTED:
        case eventTypeIds.SELL_SUBMITTED:
        case eventTypeIds.BUY_COMMITTED:
        case eventTypeIds.SELL_COMMITTED:
          toast(<OperationRelatedMessage {...listItem} />, commonProps);
          break;
        case eventTypeIds.POSTREPLY_CREATED:
          toast(<PostReplyCreatedMessage {...listItem} />, commonProps);
          break;
        case eventTypeIds.USERFOLLOWER_CREATED:
          toast(<UserFollowerCreatedMessage {...listItem} />, commonProps);
          break;
        case eventTypeIds.USERMENTION_CREATED:
          toast(<UserMentionCreatedMessage {...listItem} />, commonProps);
          break;
        case eventTypeIds.SUBSCRIPTION_AUTHORIZED:
        case eventTypeIds.SUBSCRIPTION_CANCELLED:
        case eventTypeIds.SUBSCRIPTION_PAUSED:
          toast(<SubscriptionRelatedMessage {...listItem} />, commonProps);
          break;
        default:
          break;
      }
    });
  }, [notificationListItems]);

  return (
    <>
      {children}
      {isAuthenticated && (
        <ToastContainer
          stacked
          position="bottom-left"
          autoClose={false}
          theme={theme}
        />
      )}
    </>
  );
};

export default NotificationToastsDecorator;